import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getAuth, isSignInWithEmailLink, signInWithEmailLink, signOut } from 'firebase/auth';
import { db } from '../firebase/config';
import { collection, query, where, getDocs, addDoc } from 'firebase/firestore';
import { getClientRecords, saveHairRecord, saveSkinRecord } from '../firebase/recordService';
import { signConsent, getValidConsent } from '../firebase/consentService';
import HairRecordForm from '../components/hair/HairRecordForm';
import SkinRecordForm from '../components/skin/SkinRecordForm';
import ConsentForm from '../components/common/ConsentForm';

function ClientIntake() {
  const [searchParams] = useSearchParams();
  const [clientId, setClientId] = useState(null);
  const [clientData, setClientData] = useState(null);
  const [hairRecord, setHairRecord] = useState(null);
  const [skinRecord, setSkinRecord] = useState(null);
  const [hairConsent, setHairConsent] = useState(null);
  const [skinConsent, setSkinConsent] = useState(null);
  const [activeTab, setActiveTab] = useState('hair');
  const [loading, setLoading] = useState(true);
  const [isNewClient, setIsNewClient] = useState(false);
  const [newClientName, setNewClientName] = useState('');
  const [newClientEmail, setNewClientEmail] = useState('');
  const [creating, setCreating] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showHairConsentForm, setShowHairConsentForm] = useState(true);
  const [showSkinConsentForm, setShowSkinConsentForm] = useState(true);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const auth = getAuth();

  useEffect(() => {
    const handleIntake = async () => {
      await signOut(auth).catch(() => {});
      const phoneParam = searchParams.get('phone');
      const emailParam = searchParams.get('email');

      if (!phoneParam) {
        setErrorMessage('Invalid intake link. Missing phone number.');
        setLoading(false);
        return;
      }

      if (isSignInWithEmailLink(auth, window.location.href)) {
        let email = emailParam || window.localStorage.getItem('emailForSignIn');
        if (!email) {
          setErrorMessage('Email verification link expired or invalid. Please request a new link from the salon.');
          setLoading(false);
          return;
        }
        try {
          await signInWithEmailLink(auth, email, window.location.href);
          window.localStorage.removeItem('emailForSignIn');
          await loadClientData(phoneParam, email);
        } catch (error) {
          if (error.code === 'auth/invalid-action-code') {
            setErrorMessage('This link has expired or has already been used. Please request a new intake link from the salon.');
          } else if (error.code === 'auth/email-already-in-use') {
            await loadClientData(phoneParam, email);
          } else {
            setErrorMessage('Sign-in failed: ' + error.message);
          }
          setLoading(false);
        }
      } else {
        const currentUser = auth.currentUser;
        if (currentUser) {
          await loadClientData(phoneParam, emailParam);
        } else {
          setErrorMessage('Please use the link sent to your email. Direct access not allowed.');
          setLoading(false);
        }
      }
    };
    handleIntake();
  }, []);

  const loadClientData = async (phoneNumber, userEmail = null) => {
    try {
      const clientsRef = collection(db, 'clients');
      const q = query(clientsRef, where('phoneNumber', '==', phoneNumber));
      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        const clientDoc = snapshot.docs[0];
        setClientId(clientDoc.id);
        const clientInfo = clientDoc.data();
        setClientData(clientInfo);
        const records = await getClientRecords(clientDoc.id);
        setHairRecord(records.find(r => r.recordType === 'hair'));
        setSkinRecord(records.find(r => r.recordType === 'skin'));
        
        const hairConsentData = await getValidConsent(clientDoc.id, 'hair');
        const skinConsentData = await getValidConsent(clientDoc.id, 'skin');
        setHairConsent(hairConsentData);
        setSkinConsent(skinConsentData);
        setIsNewClient(false);
      } else {
        setIsNewClient(true);
        setClientData({ phoneNumber, name: '', email: userEmail || '' });
        if (userEmail) setNewClientEmail(userEmail);
      }
    } catch (error) {
      setErrorMessage('Error loading client data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false }), 3000);
  };

  const handleCreateClient = async () => {
    if (!newClientName.trim()) {
      showNotification('Please enter your name', 'error');
      return;
    }
    setCreating(true);
    try {
      const now = new Date().toISOString();
      const newClient = {
        name: newClientName,
        phoneNumber: clientData.phoneNumber,
        email: newClientEmail,
        age: '',
        createdBy: 'client-intake',
        createdAt: now,
        updatedBy: 'client-intake',
        updatedAt: now,
      };
      const docRef = await addDoc(collection(db, 'clients'), newClient);
      setClientId(docRef.id);
      setClientData({ ...newClient, clientId: docRef.id });
      setIsNewClient(false);
      showNotification('✓ Profile created successfully!');
    } catch (error) {
      showNotification('Error creating profile: ' + error.message, 'error');
    } finally {
      setCreating(false);
    }
  };

  const handleSaveHair = async (formData, staffName) => {
    if (!clientId) return;
    try {
      await saveHairRecord(clientId, formData, staffName);
      showNotification('✓ Hair record saved');
    } catch (error) {
      showNotification('Error saving hair record: ' + error.message, 'error');
    }
  };

  const handleSaveSkin = async (formData, staffName) => {
    if (!clientId) return;
    try {
      await saveSkinRecord(clientId, formData, staffName);
      showNotification('✓ Skin record saved');
    } catch (error) {
      showNotification('Error saving skin record: ' + error.message, 'error');
    }
  };

  const handleSignConsent = async (type, formData) => {
    if (!clientId) return;
    try {
      await signConsent(clientId, type, formData);
      if (type === 'hair') {
        const updated = await getValidConsent(clientId, 'hair');
        setHairConsent(updated);
        setShowHairConsentForm(true);
      } else {
        const updated = await getValidConsent(clientId, 'skin');
        setSkinConsent(updated);
        setShowSkinConsentForm(true);
      }
      showNotification('✓ Consent signed');
    } catch (error) {
      showNotification('Error signing consent: ' + error.message, 'error');
    }
  };

  const handleExit = () => {
    window.location.href = '/thank-you';
  };

  if (loading) return <div className="p-8 text-center">Loading...</div>;

  if (errorMessage) {
    return (
      <div className="min-h-screen bg-salon-bg flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-md p-8 max-w-md text-center">
          <img src="/images/logo-grey.png" alt="Muse & Mane" className="h-16 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Link Expired or Invalid</h1>
          <p className="text-gray-600 mb-4">{errorMessage}</p>
          <p className="text-gray-500 text-sm mb-6">Please contact Muse & Mane to request a new intake link.</p>
          <button onClick={() => window.close()} className="bg-salon-button text-white px-6 py-2 rounded-md">Close Window</button>
        </div>
      </div>
    );
  }

  if (isNewClient) {
    return (
      <div className="max-w-md mx-auto p-6 bg-white rounded-lg shadow-md mt-10">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold">Welcome to Muse & Mane</h1>
          <button onClick={handleExit} className="px-3 py-1 bg-gray-200 rounded-md">Exit</button>
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Full Name *</label>
          <input type="text" value={newClientName} onChange={(e) => setNewClientName(e.target.value)} className="w-full px-3 py-2 border rounded-md" />
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Email</label>
          <input type="email" value={newClientEmail} onChange={(e) => setNewClientEmail(e.target.value)} className="w-full px-3 py-2 border rounded-md" />
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Phone Number</label>
          <input type="tel" value={clientData.phoneNumber} disabled className="w-full px-3 py-2 border rounded-md bg-gray-100" />
        </div>
        <button onClick={handleCreateClient} disabled={creating} className="w-full bg-salon-button text-white py-2 rounded-md">{creating ? 'Creating...' : 'Create Profile'}</button>
      </div>
    );
  }

  const hairConsentValid = !!hairConsent;
  const skinConsentValid = !!skinConsent;

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Notification Toast */}
      {notification.show && (
        <div className={`fixed top-20 right-4 z-50 p-4 rounded-md shadow-lg ${
          notification.type === 'success' ? 'bg-green-500' : 'bg-red-500'
        } text-white animate-pulse`}>
          {notification.message}
        </div>
      )}

      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Welcome, {clientData?.name || 'Client'}</h1>
        <button onClick={handleExit} className="px-3 py-1 bg-gray-200 rounded-md">Exit</button>
      </div>

      <div className="border-b mb-4">
        <nav className="flex gap-2">
          <button className={`px-4 py-2 rounded-t-lg ${activeTab === 'hair' ? 'bg-salon-button text-white' : 'bg-gray-200'}`} onClick={() => setActiveTab('hair')}>Hair Record</button>
          <button className={`px-4 py-2 rounded-t-lg ${activeTab === 'skin' ? 'bg-salon-button text-white' : 'bg-gray-200'}`} onClick={() => setActiveTab('skin')}>Skin Record</button>
        </nav>
      </div>

      <div className="mt-4">
        {activeTab === 'hair' && (
          <div className="space-y-6">
            <div className="border rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b">
                <h3 className="font-semibold">Step 1: Hair Service Consent</h3>
              </div>
              <div className="p-4">
                {hairConsentValid ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <p className="text-green-700">✓ Consent valid until {new Date(hairConsent.expiresAt).toLocaleDateString()}</p>
                  </div>
                ) : showHairConsentForm ? (
                  <ConsentForm 
                    clientId={clientId} 
                    clientName={clientData?.name} 
                    type="hair" 
                    onSigned={(data) => handleSignConsent('hair', data)} 
                    onCancel={() => setShowHairConsentForm(false)} 
                    isClient={true} 
                  />
                ) : (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-yellow-700">Consent not signed yet. You can sign later by coming back to this tab.</p>
                    <button onClick={() => setShowHairConsentForm(true)} className="mt-2 px-3 py-1 bg-salon-button text-white rounded-md text-sm">Sign Consent Now</button>
                  </div>
                )}
              </div>
            </div>

            <div className="border rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b">
                <h3 className="font-semibold">Step 2: Hair Record</h3>
              </div>
              <div className="p-4">
                <HairRecordForm clientId={clientId} clientName={clientData?.name} existingRecord={hairRecord} onSave={handleSaveHair} onCancel={() => {}} isClient={true}/>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'skin' && (
          <div className="space-y-6">
            <div className="border rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b">
                <h3 className="font-semibold">Step 1: Skin Service Consent</h3>
              </div>
              <div className="p-4">
                {skinConsentValid ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <p className="text-green-700">✓ Consent valid until {new Date(skinConsent.expiresAt).toLocaleDateString()}</p>
                  </div>
                ) : showSkinConsentForm ? (
                  <ConsentForm 
                    clientId={clientId} 
                    clientName={clientData?.name} 
                    type="skin" 
                    onSigned={(data) => handleSignConsent('skin', data)} 
                    onCancel={() => setShowSkinConsentForm(false)} 
                    isClient={true} 
                  />
                ) : (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-yellow-700">Consent not signed yet. You can sign later by coming back to this tab.</p>
                    <button onClick={() => setShowSkinConsentForm(true)} className="mt-2 px-3 py-1 bg-salon-button text-white rounded-md text-sm">Sign Consent Now</button>
                  </div>
                )}
              </div>
            </div>

            <div className="border rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b">
                <h3 className="font-semibold">Step 2: Skin Record</h3>
              </div>
              <div className="p-4">
                <SkinRecordForm clientId={clientId} clientName={clientData?.name} existingRecord={skinRecord} onSave={handleSaveSkin} onCancel={() => {}} isClient={true}/>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ClientIntake;